import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { LoadingButton } from "@mui/lab";
import { Box, Button, Card, Container, IconButton, Stack, Tab, Tabs, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import Iconify from "../../../minimals/components/iconify";
import Label from "../../../minimals/components/label/Label";
import CustomBreadcrumbs from "../../components/custom-breadcrumbs";
import { useSettingsContext } from "../../contexts/SettingsContext";
import { UserContext } from "../../contexts/UserContext";
import { addToExportList, getTodaysExportContactIds, isContactExportViewer } from "../../utils/exportContacts";
import { closeIFrame } from "../../utils/iframeUtils";
import { ProfileCover } from "../shared/profile";
import { ContactContext } from "./ContactContext";
import { TABS } from "./TABS";
import FeedbackButton from "../../shared/FeedbackButton";
import FeedbackModal from "../../shared/FeedbackModal";
import { createPortal } from "react-dom";
import { Global, css } from "@emotion/react";
import useResponsive from "../../../minimals/hooks/useResponsive";
import { HeaderSlotContext } from "../../components/layouts/dashboard/HeaderSlotContext";

export default function Contacts() {
  const { user } = useContext(UserContext);

  const isUserContactExportViewer = useMemo(() => isContactExportViewer(user), [user]);
  const { themeStretch, isAppInIFrame } = useSettingsContext();
  const { contact, isFetching } = useContext(ContactContext);
  const navigate = useNavigate();
  const [isContactInExportList, setIsContactInExportList] = useState(false);
  const [isAddingToExportList, setIsAddingToExportList] = useState(false);
  const isMobile = useResponsive("down", "md");
  const { slotEl } = useContext(HeaderSlotContext);
  // The desktop feedback tab is fixed and rotated down the right edge, straight
  // over the profile card on a phone.
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const coverRef = useRef<HTMLDivElement | null>(null);
  const [isCoverVisible, setIsCoverVisible] = useState(true);

  const { section } = useParams();

  const contactInteractionViewers = [
    "agoldstein@madisoncres.com",
    "sdavidowitz@madisoncres.com",
    "lhunger@madisontitle.com",
    "astrulowitz@madisontitle.com",
    "snierenberg@madisontitle.com",
    "jshapiro@madisontitle.com",
    "afriedman@madisoncres.com",
    "bfreylicher@madisoncres.com",
    "jshonek@madisontitle.com",
    "ymeister@madisontitle.com",
    "yprag@madisontitle.com",
    "jsteinberg@madison1031.com",
    "esther@madison1031.com",
    "gmargareten@madisoncres.com",
    "bsputz@madisoncres.com",
    "bfishman@madisoncres.com",
    "nschwartz@madisoncres.com",
    "tnewman@madisoncres.com",
    "jreischer@madisontitle.com",
    "eyoung@madisontitle.com",
    "tschur@madison1031.com",
    "swaldman@madisoncres.com",
    "fschwed@madisoncres.com"
  ];

  const isContactInteractionViewer = contactInteractionViewers.includes(user?.email.toLowerCase() || "");
  const filteredTabs = TABS.filter((t) => isContactInteractionViewer || t.label != "Interactions");

  const searchParams = new URLSearchParams(location.search);
  const idFromUrl = searchParams.get("id");

  const getAndSetIfContactIsInTodaysExports = async () => {
    const data = await getTodaysExportContactIds();
    setIsContactInExportList(data.includes(Number(contact?.id)));
  };

  useEffect(() => {
    if (isUserContactExportViewer && contact) {
      getAndSetIfContactIsInTodaysExports();
    }
  }, [isUserContactExportViewer, contact]);

  const contactFullName = [contact?.firstName, contact?.middleName, contact?.lastName].filter(Boolean).join(" ").trim();

  useEffect(() => {
    if (!isMobile || !coverRef.current) {
      setIsCoverVisible(true);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => setIsCoverVisible(entry.isIntersecting), {
      // Treat "behind the fixed header" as gone, not just off-screen.
      rootMargin: "-64px 0px 0px 0px"
    });
    observer.observe(coverRef.current);
    return () => observer.disconnect();
  }, [isMobile, contact?.id]);

  const selectedValue = () => {
    return filteredTabs.find((tab) => tab.values.some((value) => value === section))?.values[0];
  };

  if (isFetching) {
    return <></>;
  }

  ////If this gets use more frequently, it can be styled much better
  if (!contact) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "calc(100vh - 144px)"
        }}
      >
        <div style={{ textAlign: "center", fontSize: "50px", fontWeight: "" }}>Contact Not Found In Phonebook</div>
      </div>
    );
  }

  const addContactToExportsList = async () => {
    setIsAddingToExportList(true);
    try {
      const isAdded = await addToExportList(contact.id);
      setIsContactInExportList(isAdded);
      setIsAddingToExportList(false);
    } catch (e) {
      setIsAddingToExportList(false);
    }
  };

  const renderMobileExportButton = () => {
    if (!isUserContactExportViewer) {
      return null;
    }
    if (isContactInExportList) {
      return (
        <IconButton size="small" disabled aria-label="Added to export list" sx={{ color: "success.main" }}>
          <Iconify icon="eva:checkmark-circle-2-fill" width={20} />
        </IconButton>
      );
    }
    return (
      <IconButton
        size="small"
        aria-label="Add to export list"
        disabled={!contact.isActive || isAddingToExportList}
        onClick={addContactToExportsList}
        sx={{ border: "1px solid", borderColor: "divider" }}
      >
        <Iconify icon="eva:plus-fill" width={18} />
      </IconButton>
    );
  };

  const renderExportButton = () => {
    if (!isUserContactExportViewer) {
      return <></>;
    }

    if (isContactInExportList) {
      return (
        <Label color="success" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          Added to export list
          <Iconify icon="eva:checkmark-circle-2-fill" sx={{ mr: 0.5 }} />
        </Label>
      );
    }
    return (
      <LoadingButton
        variant="outlined"
        disabled={!contact.isActive}
        onClick={addContactToExportsList}
        startIcon={<Iconify icon="eva:plus-fill" />}
        loading={isAddingToExportList}
      >
        Add to Export List
      </LoadingButton>
    );
  };

  return (
    <>
      {isAppInIFrame && (
        <Stack direction="row" justifyContent="flex-end" paddingRight={1.5} marginBottom={1}>
          <IconButton
            size="large"
            onClick={closeIFrame}
            sx={{ position: "absolute", top: 16, right: 16, padding: 0.3 }}
          >
            <Iconify icon="eva:close-outline" width={32} height={32} />
          </IconButton>
        </Stack>
      )}
      <Helmet>
        <title> Individual Profile | TitleQ</title>
      </Helmet>

      {isMobile && (
        <Global
          styles={css`
            main {
              /* Mobile drops the breadcrumb block, which was supplying the clearance
                 under the fixed 64px header. */
              padding-top: 64px !important;
            }
          `}
        />
      )}

      {/* Same pattern as the contacts list: heading and actions render in the site
          header beside the nav button rather than costing two page rows. */}
      {isMobile && slotEl
        ? createPortal(
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ width: 1, minWidth: 0, gap: 1 }}
            >
              {/* Back sits beside the nav button, where iOS puts it. `navigate(-1)`
                  rather than a hard link so the list keeps its search and filters;
                  falls back to the list when there's no history (deep link). */}
              <IconButton
                size="small"
                aria-label="Back to contacts"
                onClick={() =>
                  window.history.state?.idx > 0 ? navigate(-1) : navigate("/phonebook/contacts/individuals")
                }
                sx={{ flexShrink: 0, ml: -0.5, color: "primary.dark" }}
              >
                <Iconify icon="eva:arrow-ios-back-fill" width={22} />
              </IconButton>
              <Typography
                noWrap
                sx={{ color: "primary.dark", fontSize: 17, fontWeight: 700, lineHeight: 1.2, minWidth: 0, flex: 1 }}
              >
                {!isCoverVisible && contactFullName ? contactFullName : "Individual Profile"}
              </Typography>
              <Stack direction="row" alignItems="center" sx={{ flexShrink: 0, gap: 1 }}>
                <IconButton
                  size="small"
                  aria-label="Submit contact update"
                  onClick={() => setIsFeedbackOpen(true)}
                  sx={{ border: "1px solid", borderColor: "divider" }}
                >
                  <Iconify icon="material-symbols:rate-review-outline" width={18} />
                </IconButton>
                {renderMobileExportButton()}
                <Button
                  size="small"
                  variant="contained"
                  disabled={!contact.isActive}
                  startIcon={<Iconify icon="eva:edit-fill" width={16} />}
                  onClick={() => {
                    navigate(`../phonebook/edit-contact?id=${contact?.id || idFromUrl}`);
                  }}
                  sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
                >
                  Edit
                </Button>
              </Stack>
            </Stack>,
            slotEl
          )
        : null}

      <Container maxWidth={themeStretch ? false : "lg"}>
        {contact && !contact.isActive && (
          <Label variant="soft" color="error">
            {" "}
            Inactive
          </Label>
        )}
        {!isMobile && (
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <CustomBreadcrumbs
              heading={`${isAppInIFrame ? "Phonebook - " : ""}Individual Profile`}
              links={[
                {
                  name: "Individuals",
                  href: "../phonebook/contacts/individuals"
                },
                {
                  name: contact?.contactRoles[0]?.branch?.company?.name || "",
                  href: contact?.contactRoles[0]?.branch?.company?.name
                    ? `../phonebook/company/profile?id=${contact?.contactRoles[0]?.branch?.company?.id}`
                    : ""
                },
                {
                  name: contact?.firstName
                    ? (contact?.firstName || "") + " " + (contact?.middleName || "") + " " + (contact?.lastName || "")
                    : ""
                }
              ]}
            />
            <Stack direction="row" spacing={2} alignItems="center">
              {renderExportButton()}
              <Button
                variant="contained"
                disabled={!contact.isActive}
                startIcon={<Iconify icon="eva:edit-fill" />}
                onClick={() => {
                  navigate(`../phonebook/edit-contact?id=${contact?.id || idFromUrl}`);
                }}
              >
                Edit
              </Button>
            </Stack>
          </Stack>
        )}
        {!isMobile && (
          <FeedbackButton
            url={window.location.href}
            destinationEmail={"phonebook@madisoncres.com"}
            subject="New phonebook update submitted"
            placeholder="Updated contact information"
            buttonText="Submit Contact Update"
          />
        )}
        {(() => {
          const tabsElement = (
            <Tabs
              value={selectedValue()}
              // Four icon+label tabs don't fit a phone; let them scroll rather than squash.
              variant={isMobile ? "scrollable" : "standard"}
              allowScrollButtonsMobile={isMobile}
              onChange={(event, newValue) => {
                navigate(`../phonebook/contact/${newValue}?id=${contact?.id || idFromUrl}`);
              }}
              sx={{
                width: isMobile ? undefined : 1,
                bgcolor: "background.paper",
                ...(isMobile
                  ? {
                      // Sticky rather than pinned to the card: the cover scrolls away,
                      // the tabs stay put under the 64px header, and the header title
                      // takes over showing whose profile this is.
                      position: "sticky",
                      top: 64,
                      zIndex: 10,
                      mb: 2,
                      // Break out of the Container's gutter so the strip runs edge to
                      // edge. Matching the content width made it read as a piece of
                      // the card below rather than as an extension of the header.
                      mx: { xs: -2, sm: -3 },
                      width: "auto",
                      borderBottom: "1px solid",
                      borderColor: "divider",
                      // Once the cover is gone the strip is floating over white
                      // content, so it needs a shadow to read as a layer above it
                      // rather than as part of the card below.
                      transition: "box-shadow 150ms",
                      boxShadow: isCoverVisible ? "none" : "0 8px 12px -8px rgba(22, 28, 36, 0.36)"
                    }
                  : {
                      bottom: 0,
                      zIndex: 9,
                      position: "absolute"
                    }),
                "& .MuiTabs-flexContainer": {
                  pl: { xs: 2, md: 0 },
                  pr: { md: 3 },
                  justifyContent: {
                    sm: "center",
                    md: "flex-end"
                  }
                }
              }}
            >
              {filteredTabs.map((tab) => (
                <Tab key={tab.values[0]} value={tab.values[0]} icon={tab.icon} label={tab.label} />
              ))}
            </Tabs>
          );

          const coverCard = (
            <Card
              ref={coverRef}
              sx={{
                mb: { xs: 0, md: 3 },
                // Desktop's 280 includes the tab strip overlaid at its bottom edge;
                // on mobile the tabs have moved out, so this is cover only.
                height: { xs: 110, md: 280 },
                position: "relative"
              }}
            >
              <ProfileCover
                name={`${contact?.firstName || ""} ${contact?.middleName || ""} ${contact?.lastName || ""}`}
                suffix={contact?.suffix}
                logo={
                  contact?.contactRoles[0]?.businessEmail?.split("@")[1]
                    ? `https://logo.clearbit.com/${contact?.contactRoles[0]?.businessEmail?.split("@")[1]}`
                    : undefined
                }
                role={contact?.contactRoles[0]?.role?.name || ""}
                company={contact?.contactRoles[0]?.branch?.company}
              />
              {!isMobile && tabsElement}
            </Card>
          );

          return (
            <>
              {coverCard}
              {isMobile && tabsElement}
            </>
          );
        })()}
        {filteredTabs.map(
          (tab) => tab.values.some((v) => v === section) && <Box key={tab.values[0]}> {tab.component}</Box>
        )}
      </Container>

      {isFeedbackOpen && (
        <FeedbackModal
          onClose={() => setIsFeedbackOpen(false)}
          open={isFeedbackOpen}
          url={window.location.href}
          destinationEmail={"phonebook@madisoncres.com"}
          subject="New phonebook update submitted"
          placeholder="Updated contact information"
        />
      )}
    </>
  );
}
